import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ChevronDown } from 'lucide-react'
import navConfig, { NavItem } from './navConfig'

/**
 * Hook to persist state in localStorage
 */
function useLocalStorage<T>(key: string, initialValue: T): [T, React.Dispatch<React.SetStateAction<T>>] {
  const [value, setValue] = useState(() => {
    try {
      const stored = window.localStorage.getItem(key)
      return stored ? JSON.parse(stored) : initialValue
    } catch {
      return initialValue
    }
  })
  useEffect(() => {
    try {
      window.localStorage.setItem(key, JSON.stringify(value))
    } catch {
      /* ignore */
    }
  }, [key, value])
  return [value, setValue]
}

/**
 * Sidebar navigation component
 */
export default function SidebarNav() {
  // track which groups are open (group label -> open state)
  const [openGroups, setOpenGroups] = useLocalStorage<Record<string, boolean>>(
    'sidebar-open-groups',
    {} as Record<string, boolean>
  )
  const location = useLocation()

  // ensure current path group is open
  useEffect(() => {
    navConfig.forEach((group) => {
      group.children?.forEach((item) => {
        if (item.href && location.pathname.startsWith(item.href)) {
          setOpenGroups(prev => {
            if (!prev[group.label]) {
              return { ...prev, [group.label]: true }
            }
            return prev
          })
        }
      })
    })
  }, [location.pathname, setOpenGroups])

  const toggleGroup = (label: string) => {
    setOpenGroups({ ...openGroups, [label]: !openGroups[label] })
  }

  return (
    <div className="space-y-1">
        {navConfig.map((group: NavItem) => {
        const isOpen = !!openGroups[group.label]
        return (
          <div key={group.label}>
            {/* Group header */}
            <button
              onClick={() => toggleGroup(group.label)}
              className="flex items-center w-full py-2 px-3 rounded-lg hover:bg-gray-800 transition-colors duration-200 text-gray-300 hover:text-white group"
            >
              <span className="flex items-center">
                {React.isValidElement(group.icon)
                  ? React.cloneElement(group.icon, {
                      className: 'w-5 h-5 mr-3 text-blue-500 group-hover:text-blue-400',
                    })
                  : null}
                <span className="font-medium">{group.label}</span>
              </span>
              <ChevronDown
                className={`w-4 h-4 ml-auto transform transition-transform duration-200 ${
                  isOpen ? 'rotate-180' : ''
                }`} />
            </button>
            {/* Sub-items */}
            {isOpen && group.children && (
              <div className="mt-1 space-y-0.5">
                {group.children.map((item) => (
                  <div key={item.label} className="flex items-center">
                    {item.href && !item.comingSoon ? (
                      <Link
                        to={item.href}
                        className="flex items-center w-full py-2 px-3 rounded-lg hover:bg-gray-800 transition-colors duration-200 text-gray-300 hover:text-white group ml-8"
                      >
                        {React.isValidElement(item.icon)
                          ? React.cloneElement(item.icon, {
                              className: 'w-5 h-5 mr-3 text-blue-500 group-hover:text-blue-400',
                            })
                          : null}
                        <span className="text-sm">{item.label}</span>
                      </Link>
                    ) : (
                      <div
                        className="flex items-center w-full py-2 px-3 rounded-lg cursor-not-allowed text-gray-400 group ml-8"
                        title="Expected Q3 release"
                      >
                        {React.isValidElement(item.icon)
                          ? React.cloneElement(item.icon, {
                              className: 'w-5 h-5 mr-3',
                            })
                          : null}
                        <span className="text-sm">{item.label}</span>
                        <span className="ml-2 text-xs bg-amber-100 text-amber-800 px-1 rounded">soon</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}